/**
 * The role ladder: how "visible to <role>" gates read.
 *
 * Authority roles are ranked by `sortOrder` (higher = more senior), and a gate
 * set to one of them opens for that role *and every authority role ranked
 * above it* — a Game Master sees what's set to Raid Leader, a Raid Leader what's
 * set to Member. Game roles ("Plays Among Us") are interests, not rungs: a gate
 * set to one opens only for the members who play that game. God sees everything.
 *
 * The server works out each viewer's reach once (see buildViewer) and ships it
 * on the Viewer, so the nav, page modules and gallery albums all answer the same
 * question the same way, on either side.
 */

import type { Viewer } from './permissions';

export interface LadderRole {
  id: number;
  kind: 'authority' | 'game';
  sortOrder: number;
}

/**
 * Every role whose gated content a member holding `held` may see: the roles
 * they hold, plus each authority role ranked strictly below their highest one.
 * Two roles on the same rung don't open each other's gates.
 */
export function roleReach(held: number[], all: LadderRole[]): number[] {
  const mine = new Set(held);
  let top: number | null = null;
  for (const r of all) {
    if (r.kind === 'authority' && mine.has(r.id) && (top == null || r.sortOrder > top)) top = r.sortOrder;
  }
  const reach = new Set(held);
  if (top != null) {
    for (const r of all) if (r.kind === 'authority' && r.sortOrder < top) reach.add(r.id);
  }
  return [...reach].sort((a, b) => a - b);
}

/** Can this viewer see something gated to `roleId`? */
export function canSeeRole(viewer: Pick<Viewer, 'isGod' | 'roleReach'> | null, roleId: number): boolean {
  return !!viewer && (viewer.isGod || viewer.roleReach.includes(roleId));
}

/**
 * How a gate set to `role` reads in a picker or badge: the top rung by name,
 * lower rungs "and up", game roles as they are.
 */
export function roleGateLabel(role: LadderRole & { name: string }, all: LadderRole[]): string {
  if (role.kind !== 'authority') return role.name;
  const higher = all.some((r) => r.kind === 'authority' && r.sortOrder > role.sortOrder);
  return higher ? `${role.name} and up` : role.name;
}

/** Authority roles top rung first, then game roles by name: the order pickers list them in. */
export function ladderOrder<T extends LadderRole & { name: string }>(roles: T[]): { ladder: T[]; games: T[] } {
  return {
    ladder: roles.filter((r) => r.kind === 'authority').sort((a, b) => b.sortOrder - a.sortOrder || a.name.localeCompare(b.name)),
    games: roles.filter((r) => r.kind === 'game').sort((a, b) => a.name.localeCompare(b.name)),
  };
}
